import { Help, LocalLaundryService, Terminal } from '@mui/icons-material';
import CampaignIcon from '@mui/icons-material/Campaign';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import HandymanIcon from '@mui/icons-material/Handyman';
import OpacityIcon from '@mui/icons-material/Opacity';
import PeopleIcon from '@mui/icons-material/People';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import PropaneIcon from '@mui/icons-material/Propane';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { Box, Button, Collapse, List, ListItem, styled, Tooltip } from '@mui/material';
import { useContext, useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import {
  BrewersIcon,
  ClientsIcon,
  ProductsIcon,
  RefMaterialsIcon,
  ReportsIcon,
  SettingsIcon,
  UsersIcon,
  VenuesIcon,
  VisitsIcon
} from '../../../../assets/custom_icons/customIcons';
import { PermissionsGuard } from '../../../../content/routes';
import { SidebarContext } from '../../../../contexts';

export const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding-top: 0;
    padding-bottom: 0;

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

  .MuiListSubheader-root {
    text-transform: uppercase;
    font-family: 'LLBrown_Bold',
    font-size: ${theme.typography.pxToRem(12)};
    color: ${theme.colors.alpha.trueWhite[50]};
    padding: ${theme.spacing(0, 2.5)};
    line-height: 1.4;
  }`
);

export const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {
      padding-left: 0;

      > .MuiListItem-root {
        
        margin-bottom: 3px;

        > .MuiButton-root {
          width: auto;
        }

        > button > span:not(.MuiButton-startIcon) {
          opacity: 1;
          transition: opacity .3s;
          animation: menuTextAnimation .3s forwards;
          overflow: hidden;
          white-space: nowrap;
          display: flex;
          align-items: center;
        }

        .av-collapsed & {
          .MuiListItem-root {
            margin-bottom: 4px;
          }

          > button > span:not(.MuiButton-startIcon) {
            opacity: 0;
            display: none;
          }
        }
      }

      .MuiListItem-root {
        padding: 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};
        }

        .MuiButton-root {
          display: flex;
          color: ${theme.colors.alpha.trueWhite[70]};
          background-color: transparent;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 0)};
          padding-left: 14px;
          padding-right: 10px;
          border: 1px solid transparent;
          border-radius: 30px;

          .av-collapsed & {
            min-width: auto;
          }

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            color: ${theme.colors.alpha.white[100]};
            transition: ${theme.transitions.create(['color'])};
            margin-right: 10px;

            .MuiSvgIcon-root {
              font-size: 32px;
              transition: none;
            }

            .av-collapsed & {
              margin-right: 0;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.colors.alpha.trueWhite[100]};
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: 4px;
          }
          
          .MuiButton-endIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${theme.colors.alpha.white[30]};
            color: ${theme.colors.alpha.trueWhite[100]};
            
            .av-collapsed & {
              border: 1px solid ${theme.colors.alpha.trueWhite[100]};
            }
            
            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.alpha.trueWhite[100]};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: 0 8px;
          }

          .MuiListItem-root {
            padding: 0;
            margin-bottom: ${theme.spacing(1)};

            &.first {
              margin-top: ${theme.spacing(1)};
            }

            &.last {
              margin-bottom: ${theme.spacing(1)};
            }

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 2)};
              margin-left: ${theme.spacing(4)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &.active,
              &:hover {
                &:before {
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }`
);

export function SidebarMenu<SidebarMenuProps>({ wide = false }) {
  const { closeSidebar, changeUsersSubMenuToggle, changeBrewersTSPsSubMenuToggle, changeReportsSubMenuToggle } =
    useContext(SidebarContext);
  const [usersMenu, setUsersMenu] = useState(false);
  const [brewersTSPsMenu, setBrewersTSPsMenuMenu] = useState(false);
  const [reportsMenu, setReportsMenu] = useState(false);

  const handleUsersClick = (): void => {
    if (wide) {
      setUsersMenu(!usersMenu);
    } else {
      changeUsersSubMenuToggle();
    }
  };

  const handleBrewersTSPsClick = (): void => {
    if (wide) {
      setBrewersTSPsMenuMenu(!brewersTSPsMenu);
    } else {
      changeBrewersTSPsSubMenuToggle();
    }
  };

  const handleReportsClick = (): void => {
    if (wide) {
      setReportsMenu(!reportsMenu);
    } else {
      changeReportsSubMenuToggle();
    }
  };

  const withMenuTooltip = (title: string, content: JSX.Element): JSX.Element => {
    if (wide) {
      return content;
    }

    return (
      <Tooltip arrow placement="right" title={title}>
        {content}
      </Tooltip>
    );
  };

  return (
    <>
      <MenuWrapper>
        <PermissionsGuard allowedPermissions={['visitViewAll', 'visitViewAllClient', 'visitViewAllVenue']}>
          <List component="div">
            <SubMenuWrapper>
              <List component="div">
                <ListItem component="div">
                  {withMenuTooltip(
                    'Visits',
                    <Button
                      disableRipple
                      className="level-1"
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/visits"
                      startIcon={VisitsIcon}
                    >
                      {wide && 'Visits'}
                    </Button>
                  )}
                </ListItem>
              </List>
            </SubMenuWrapper>
          </List>
        </PermissionsGuard>

        <PermissionsGuard allowedPermissions={['viewAllUsers', 'viewClientUsers', 'viewVenueUsers']}>
          <List component="div">
            <SubMenuWrapper>
              <List component="div">
                <ListItem component="div">
                  {withMenuTooltip(
                    'Users',
                    <Button
                      disableRipple
                      className="level-1"
                      type="button"
                      onClick={handleUsersClick}
                      startIcon={UsersIcon}
                    >
                      <span>Users {usersMenu ? <ExpandLess /> : <ExpandMore />}</span>
                    </Button>
                  )}
                </ListItem>
                {wide && (
                  <div>
                    <ListItem component="div">
                      <Collapse in={usersMenu} timeout="auto" unmountOnExit>
                        <ListItem component="div" className="first">
                          <Button
                            disableRipple
                            component={RouterLink}
                            onClick={closeSidebar}
                            to="/users/list"
                            startIcon={<PeopleIcon />}
                          >
                            List
                          </Button>
                        </ListItem>
                      </Collapse>
                    </ListItem>
                    <PermissionsGuard allowedPermissions={['viewUserRole']}>
                      <ListItem component="div">
                        <Collapse in={usersMenu} timeout="auto" unmountOnExit>
                          <ListItem component="div">
                            <Button
                              disableRipple
                              component={RouterLink}
                              onClick={closeSidebar}
                              to="/users/roles"
                              startIcon={<SecurityIcon />}
                            >
                              Roles
                            </Button>
                          </ListItem>
                        </Collapse>
                      </ListItem>
                    </PermissionsGuard>
                    <PermissionsGuard allowedPermissions={['viewUserPermission']}>
                      <ListItem component="div">
                        <Collapse in={usersMenu} timeout="auto" unmountOnExit>
                          <ListItem component="div" className="last">
                            <Button
                              disableRipple
                              component={RouterLink}
                              onClick={closeSidebar}
                              to="/users/permissions"
                              startIcon={<VerifiedUserIcon />}
                            >
                              Permissions
                            </Button>
                          </ListItem>
                        </Collapse>
                      </ListItem>
                    </PermissionsGuard>
                  </div>
                )}
              </List>
            </SubMenuWrapper>
          </List>
        </PermissionsGuard>

        <PermissionsGuard allowedPermissions={['venueViewAll', 'venueViewAllClient', 'venueViewAllVenue']}>
          <List component="div">
            <SubMenuWrapper>
              <List component="div">
                <ListItem component="div">
                  {withMenuTooltip(
                    'Venues',
                    <Button
                      disableRipple
                      className="level-1"
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/venues/list"
                      startIcon={VenuesIcon}
                    >
                      {wide && 'Venues'}
                    </Button>
                  )}
                </ListItem>
              </List>
            </SubMenuWrapper>
          </List>
        </PermissionsGuard>

        <PermissionsGuard allowedPermissions={['clientViewAll']}>
          <List component="div">
            <SubMenuWrapper>
              <List component="div">
                <ListItem component="div">
                  {withMenuTooltip(
                    'Clients',
                    <Button
                      disableRipple
                      className="level-1"
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/clients"
                      startIcon={ClientsIcon}
                    >
                      {wide && 'Clients'}
                    </Button>
                  )}
                </ListItem>
              </List>
            </SubMenuWrapper>
          </List>
        </PermissionsGuard>

        <PermissionsGuard allowedPermissions={['brewerViewAll']}>
          <List component="div">
            <SubMenuWrapper>
              <List component="div">
                <ListItem component="div">
                  {withMenuTooltip(
                    'Brewers',
                    <Button
                      disableRipple
                      className="level-1"
                      type="button"
                      onClick={handleBrewersTSPsClick}
                      startIcon={BrewersIcon}
                      style={{ display: 'flex', flexWrap: 'nowrap' }}
                    >
                      <span>Brewers {brewersTSPsMenu ? <ExpandLess /> : <ExpandMore />}</span>
                    </Button>
                  )}
                </ListItem>
                {wide && (
                  <div>
                    <PermissionsGuard allowedPermissions={['brewerViewAll']}>
                      <ListItem component="div">
                        <Collapse in={brewersTSPsMenu} timeout="auto" unmountOnExit>
                          <ListItem component="div" className="first">
                            <Button
                              disableRipple
                              component={RouterLink}
                              onClick={closeSidebar}
                              to="/brewers"
                              startIcon={BrewersIcon}
                            >
                              {wide && 'Brewers'}
                            </Button>
                          </ListItem>
                        </Collapse>
                      </ListItem>
                    </PermissionsGuard>

                    <PermissionsGuard allowedPermissions={['tspViewAll']}>
                      <ListItem component="div">
                        <Collapse in={brewersTSPsMenu} timeout="auto" unmountOnExit>
                          <ListItem component="div" className="last">
                            <Button
                              disableRipple
                              component={RouterLink}
                              onClick={closeSidebar}
                              to="/technical-support-providers/list"
                              startIcon={<HandymanIcon />}
                            >
                              TSPs
                            </Button>
                          </ListItem>
                        </Collapse>
                      </ListItem>
                    </PermissionsGuard>

                    <PermissionsGuard allowedPermissions={['amsPartsViewAll']}>
                      <ListItem component="div">
                        <Collapse in={brewersTSPsMenu} timeout="auto" unmountOnExit>
                          <ListItem component="div" className="last">
                            <Button
                              disableRipple
                              component={RouterLink}
                              onClick={closeSidebar}
                              to="/ams/list"
                              startIcon={<PlumbingIcon />}
                            >
                              AMS Parts
                            </Button>
                          </ListItem>
                        </Collapse>
                      </ListItem>
                    </PermissionsGuard>
                  </div>
                )}
              </List>
            </SubMenuWrapper>
          </List>
        </PermissionsGuard>

        <List component="div">
          <SubMenuWrapper>
            <List component="div">
              <PermissionsGuard allowedPermissions={['productViewAll']}>
                <ListItem component="div">
                  {withMenuTooltip(
                    'Products',
                    <Button
                      disableRipple
                      className="level-1"
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/products"
                      startIcon={ProductsIcon}
                    >
                      {wide && 'Products'}
                    </Button>
                  )}
                </ListItem>
              </PermissionsGuard>
              <PermissionsGuard allowedPermissions={['refMaterialViewAll']}>
                <ListItem component="div">
                  {withMenuTooltip(
                    'Reference Materials',
                    <Button
                      disableRipple
                      className="level-1"
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/ref-materials"
                      startIcon={RefMaterialsIcon}
                    >
                      {wide && 'Reference Materials'}
                    </Button>
                  )}
                </ListItem>
              </PermissionsGuard>
              <PermissionsGuard allowedPermissions={['reportsVisitsGenViewAll']}>
                <SubMenuWrapper>
                  <List component="div">
                    <ListItem component="div">
                      {withMenuTooltip(
                        'Reports',
                        <Button
                          disableRipple
                          className="level-1"
                          type="button"
                          onClick={handleReportsClick}
                          startIcon={ReportsIcon}
                          style={{ display: 'flex', flexWrap: 'nowrap' }}
                        >
                          <span>Reports {reportsMenu ? <ExpandLess /> : <ExpandMore />}</span>
                        </Button>
                      )}
                    </ListItem>
                    {wide && (
                      <div>
                        <PermissionsGuard allowedPermissions={['reportsVisitsGenViewAll']}>
                          <ListItem component="div">
                            <Collapse in={reportsMenu} timeout="auto" unmountOnExit>
                              <ListItem component="div" className="first">
                                <Button
                                  disableRipple
                                  component={RouterLink}
                                  onClick={closeSidebar}
                                  to="/reports/visits-general"
                                  startIcon={VisitsIcon}
                                >
                                  {wide && 'Visits General'}
                                </Button>
                              </ListItem>
                            </Collapse>
                          </ListItem>
                        </PermissionsGuard>

                        <PermissionsGuard allowedPermissions={['reportsClientEstateViewAll']}>
                          <ListItem component="div">
                            <Collapse in={reportsMenu} timeout="auto" unmountOnExit>
                              <ListItem component="div" className="last">
                                <Button
                                  disableRipple
                                  component={RouterLink}
                                  onClick={closeSidebar}
                                  to="/reports/client-estate"
                                  startIcon={ClientsIcon}
                                >
                                  {wide && 'Client Estate'}
                                </Button>
                              </ListItem>
                            </Collapse>
                          </ListItem>
                        </PermissionsGuard>

                        <PermissionsGuard allowedPermissions={['reportsVisitsClientViewAll']}>
                          <ListItem component="div">
                            <Collapse in={reportsMenu} timeout="auto" unmountOnExit>
                              <ListItem component="div" className="last">
                                <Button
                                  disableRipple
                                  component={RouterLink}
                                  onClick={closeSidebar}
                                  to="/reports/visits-client"
                                  startIcon={VisitsIcon}
                                >
                                  {wide && 'Visits Client'}
                                </Button>
                              </ListItem>
                            </Collapse>
                          </ListItem>
                        </PermissionsGuard>

                        <PermissionsGuard allowedPermissions={['reportsAmsPartsViewAll']}>
                          <ListItem component="div">
                            <Collapse in={reportsMenu} timeout="auto" unmountOnExit>
                              <ListItem component="div" className="last">
                                <Button
                                  disableRipple
                                  component={RouterLink}
                                  onClick={closeSidebar}
                                  to="/reports/ams-parts"
                                  startIcon={<PlumbingIcon />}
                                >
                                  {wide && 'Ams Parts Used'}
                                </Button>
                              </ListItem>
                            </Collapse>
                          </ListItem>
                        </PermissionsGuard>

                        <PermissionsGuard allowedPermissions={['reportsWastageViewAll']}>
                          <ListItem component="div">
                            <Collapse in={reportsMenu} timeout="auto" unmountOnExit>
                              <ListItem component="div" className="last">
                                <Button
                                  disableRipple
                                  component={RouterLink}
                                  onClick={closeSidebar}
                                  to="/reports/visit-taps"
                                  startIcon={<OpacityIcon />}
                                >
                                  {wide && 'Wastage'}
                                </Button>
                              </ListItem>
                            </Collapse>
                          </ListItem>
                        </PermissionsGuard>

                        <PermissionsGuard allowedPermissions={['reportsWastageViewAll']}>
                          <ListItem component="div">
                            <Collapse in={reportsMenu} timeout="auto" unmountOnExit>
                              <ListItem component="div" className="last">
                                <Button
                                  disableRipple
                                  component={RouterLink}
                                  onClick={closeSidebar}
                                  to="/reports/visit-washers"
                                  startIcon={<LocalLaundryService />}
                                >
                                  {wide && 'Glass Washers'}
                                </Button>
                              </ListItem>
                            </Collapse>
                          </ListItem>
                        </PermissionsGuard>

                        <PermissionsGuard allowedPermissions={['reportsGasCopViewAll']}>
                          <ListItem component="div">
                            <Collapse in={reportsMenu} timeout="auto" unmountOnExit>
                              <ListItem component="div" className="last">
                                <Button
                                  disableRipple
                                  component={RouterLink}
                                  onClick={closeSidebar}
                                  to="/reports/gas-cop"
                                  startIcon={<PropaneIcon />}
                                >
                                  {wide && 'Gas COP Expiry'}
                                </Button>
                              </ListItem>
                            </Collapse>
                          </ListItem>
                        </PermissionsGuard>
                      </div>
                    )}
                  </List>
                </SubMenuWrapper>
              </PermissionsGuard>

              <PermissionsGuard allowedPermissions={['settingsView']}>
                <ListItem component="div">
                  {withMenuTooltip(
                    'Settings',
                    <Button
                      disableRipple
                      className="level-1"
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/settings/legals"
                      startIcon={SettingsIcon}
                    >
                      {wide && 'Settings'}
                    </Button>
                  )}
                </ListItem>
              </PermissionsGuard>

              <PermissionsGuard allowedPermissions={['bannerView']}>
                <ListItem component="div">
                  {withMenuTooltip(
                    'Banners',
                    <Button
                      disableRipple
                      className="level-1"
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/banners/list"
                      startIcon={<CampaignIcon />}
                    >
                      {wide && 'Banners'}
                    </Button>
                  )}
                </ListItem>
              </PermissionsGuard>

              <PermissionsGuard allowedPermissions={['viewLogs']}>
                <ListItem component="div">
                  {withMenuTooltip(
                    'Logs',
                    <Button
                      disableRipple
                      className="level-1"
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/logs"
                      startIcon={<Terminal />}
                    >
                      {wide && 'Logs'}
                    </Button>
                  )}
                </ListItem>
              </PermissionsGuard>

              <PermissionsGuard allowedPermissions={['articleViewAll']}>
                <ListItem component="div">
                  {withMenuTooltip(
                    'Help',
                    <Button
                      disableRipple
                      className="level-1"
                      component={RouterLink}
                      onClick={closeSidebar}
                      to="/help-articles/list"
                      startIcon={<Help />}
                    >
                      {wide && 'Help'}
                    </Button>
                  )}
                </ListItem>
              </PermissionsGuard>
            </List>
          </SubMenuWrapper>
        </List>
      </MenuWrapper>
    </>
  );
}
